import { createAction } from "@reduxjs/toolkit";
import {
  delayBeforeHideFailureMessage,
  delayBeforeHideSuccessMessage
} from "../../config/constants";
import {
  hideFailureMessage,
  hideSuccessMessage,
  showFailureMessage,
  showSuccessMessage
} from "../../store/statusMessages";
import { exists, getErrorMessageFromRequest } from "../../utils/utils";
import { editOrganizationsRoles } from "../accounts/accounts";
import * as actionTypes from "./actionTypes";

export const setChanges = createAction(actionTypes.SET_CHANGES);
export const setFineGranularityChanges = createAction(actionTypes.SET_FINE_GRANULARITY_CHANGES);
export const resetChanges = createAction(actionTypes.RESET_CHANGES);

const changesAreEqual = (change1, change2) =>
  change1.permission === change2.permission && change1.role === change2.role;

export const handleChange = newChange => (dispatch, getState) => {
  const changes = getState().rolesPermissions.changes;
  const shouldAdd = !exists(changes.find(change => changesAreEqual(change, newChange)));
  if (shouldAdd) {
    return dispatch(setChanges(changes.concat(newChange)));
  }
  return dispatch(setChanges(changes.filter(change => !changesAreEqual(change, newChange))));
};
export const handleFineGranularityChange = newChange => (dispatch, getState) => {
  const changes = getState().rolesPermissions.fineGranularityChanges;
  const role = getState().accounts.roles.find(role => role.id === newChange.role);
  const fineGranularity = role.aclRolesFineGrainedResourcesActions.find(
    permission => permission.fineGrainedResourceActionId === newChange.fineGrainedPermission
  );
  const defaultIds = fineGranularity?.ids || [];
  const areEqual =
    newChange.ids.reduce(
      (partiallyEqual, id) =>
        partiallyEqual && exists(defaultIds.find(defaultId => defaultId === id)),
      true
    ) && newChange.ids.length === defaultIds.length;
  const filteredChanges = changes.filter(
    change =>
      change.role !== newChange.role ||
      change.fineGrainedPermission !== newChange.fineGrainedPermission
  );
  if (areEqual) return dispatch(setFineGranularityChanges(filteredChanges));
  return dispatch(setFineGranularityChanges(filteredChanges.concat(newChange)));
};

export const saveChanges = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const changes = state.rolesPermissions.changes;
    const fineGranularityChanges = state.rolesPermissions.fineGranularityChanges;
    const modifiedRoles = state.accounts.roles
      .filter(
        role =>
          exists(changes.find(change => change.role === role.id)) ||
          exists(fineGranularityChanges.find(change => change.role === role.id))
      )
      .reduce(
        (object, role) =>
          Object.assign(object, {
            [role.id]: {
              permissions: role.aclResourcesActions.map(permission => permission.id),
              fineGrainedPermissions: fineGranularityChanges
            }
          }),
        {}
      );
    changes.forEach(change => {
      let permissions = modifiedRoles[change.role].permissions;
      const index = permissions.indexOf(change.permission);
      if (index < 0) {
        permissions = permissions.concat(change.permission);
      } else {
        permissions.splice(index, 1);
      }
      modifiedRoles[change.role].permissions = permissions;
    });
    try {
      await dispatch(editOrganizationsRoles(modifiedRoles));
      dispatch(resetChanges());
      dispatch(showSuccessMessage("Changes were succesfully saved!"));
      setTimeout(() => dispatch(hideSuccessMessage()), delayBeforeHideSuccessMessage);
    } catch (error) {
      dispatch(
        showFailureMessage(
          "Error while saving organizations roles",
          getErrorMessageFromRequest(error)
        )
      );
      return setTimeout(() => dispatch(hideFailureMessage()), delayBeforeHideFailureMessage);
    }
  };
};
